import axios from 'axios';
import axiosRetry from 'axios-retry';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Service Leads (Phase 2 — car-service vertical CRM inbox)
export const serviceLeadsApi = {
  list: (params) => axios.get(`${API_URL}/service-leads`, { params }),
  counts: (params) => axios.get(`${API_URL}/service-leads/counts`, { params }),
  get: (id) => axios.get(`${API_URL}/service-leads/${id}`),
  create: (body) => axios.post(`${API_URL}/service-leads`, body),
  patch: (id, body) => axios.patch(`${API_URL}/service-leads/${id}`, body),
  addActivity: (id, body) => axios.post(`${API_URL}/service-leads/${id}/activities`, body),
  contact: (id, body) => axios.post(`${API_URL}/service-leads/${id}/contact`, body || {}),
  quote: (id, body) => axios.post(`${API_URL}/service-leads/${id}/quote`, body),
  book: (id, body) => axios.post(`${API_URL}/service-leads/${id}/book`, body),
  cancel: (id, body) => axios.post(`${API_URL}/service-leads/${id}/cancel`, body),
  reassign: (id, body) => axios.post(`${API_URL}/service-leads/${id}/reassign`, body),
  // Surepass-backed RC lookup → returns normalized {brand, model, fuel_type, body_class, year}
  lookupRC: (rcNumber, { forceRefresh = false } = {}) =>
    axios.get(`${API_URL}/service-leads/lookup-rc/${encodeURIComponent(rcNumber)}`,
      { params: forceRefresh ? { force_refresh: true } : {} }),
  // Generate Razorpay link + WhatsApp it to customer (with package + slot)
  sendPaymentLink: (id, body) => axios.post(`${API_URL}/service-leads/${id}/send-payment-link`, body),
  // Force-fetch Razorpay payment_link status + reconcile CRM if Razorpay
  // disagrees (paid_amount / payment_status diff). Returns {changed, diff}.
  recheckPayment: (id) => axios.post(`${API_URL}/service-leads/${id}/recheck-payment`),
  // Cancel the lead's active Razorpay link so it can't be paid → LINK_CANCELLED.
  // body: { reason, reason_note } (optional).
  cancelPaymentLink: (id, body) => axios.post(`${API_URL}/service-leads/${id}/cancel-payment-link`, body || {}),
  // One-tap reissue: re-send a link using the lead's stored quote (same amount).
  reissuePaymentLink: (id) => axios.post(`${API_URL}/service-leads/${id}/reissue-payment-link`),
  // Active links expiring within N hours (expiry nudge banner). Scoped by role.
  expiringLinks: (withinHours = 6) => axios.get(`${API_URL}/service-leads/expiring-links`, { params: { within_hours: withinHours } }),
  // Customer history — all past leads for a given mobile
  byCustomer: (mobile, params) => axios.get(`${API_URL}/service-leads/by-customer/${encodeURIComponent(mobile)}`, { params }),
  // Status catalogue + allowed-transition map for the row-level status dropdown
  statuses: () => axios.get(`${API_URL}/service-leads/statuses`),
  // Eligible service-sales reps for reassign (in_city[] / out_of_city[] buckets)
  eligibleReps: (city) => axios.get(`${API_URL}/service-leads/eligible-reps`, { params: city ? { city } : {} }),
  // ── Founder-only tools (Sonu, 2026-10-07). All three 403 for everyone else,
  // which is exactly how the UI decides whether to render them.
  // Lead count per employee for the Employee filter. Honours every other
  // filter on screen but NOT the employee one.
  employeeCounts: (params) => axios.get(`${API_URL}/service-leads/employee-counts`, { params }),
  // Who a lead may be handed to — driven by the SERVICE_SALES_EXEC role, so a
  // new hire becomes selectable with no deploy.
  reassignTargets: () => axios.get(`${API_URL}/service-leads/reassign-targets`),
  // Move many leads at once: either explicit `lead_ids`, or
  // `select_all_matching` + the filters on screen. Always send
  // `expected_count` — the server 409s rather than move a different set.
  bulkReassign: (body) => axios.post(`${API_URL}/service-leads/bulk-reassign`, body),
  // Collapse all OPEN duplicate rows for one customer into the latest one.
  // Used by the in-modal "Merge duplicates" CTA in ServiceLeadsPage. Backend
  // is idempotent and admin-only (CEO/SALES_HEAD/COUNTRY_HEAD/ADMIN). Optional
  // keeperId forces a specific lead to be the keeper (over the auto-pick).
  dedupeCustomer: (mobile, keeperId = null) => axios.post(
    `${API_URL}/service-leads/admin/dedupe-customer/${encodeURIComponent(mobile)}`,
    keeperId ? { keeper_id: keeperId } : {},
  ),
  // Inbox-level summary: how many customers currently have OPEN duplicates?
  // Drives the amber "Merge All Duplicates" banner at the top of Service Leads.
  duplicateCustomers: () => axios.get(`${API_URL}/service-leads/admin/duplicate-customers`),
  // Per-customer breakdown — which row gets KEPT, which get DELETED. Powers
  // the detailed merge preview modal so reps can see exactly what's happening.
  duplicateCustomersPreview: () => axios.get(
    `${API_URL}/service-leads/admin/duplicate-customers/preview`,
  ),
  // Sweep + merge all customers' duplicates in one shot. `dryRun=true` previews
  // counts without mutating. Admin-only. `overrides` is an optional
  // {mobile: keeper_lead_id} map forcing the keeper per group.
  dedupeAllCustomers: (dryRun = false, overrides = null) => axios.post(
    `${API_URL}/service-leads/admin/dedupe-all-customers?dry_run=${dryRun ? 'true' : 'false'}`,
    overrides && Object.keys(overrides).length ? { overrides } : {},
  ),
  // 2026-06-03: WhatsApp reminder parity with the inspection vertical.
  // POST schedules / re-schedules the reminder; DELETE cancels it.
  // Background scheduler fires Twilio ~10 min before reminder_date+time (IST).
  setReminder: (id, body) => axios.post(`${API_URL}/service-leads/${id}/reminder`, body),
  clearReminder: (id) => axios.delete(`${API_URL}/service-leads/${id}/reminder`),
};

// Service Packages (admin CRUD + read-only helpers for CRM quoting)
export const servicePackagesApi = {
  list: (params) => axios.get(`${API_URL}/service-packages`, { params }),
  get: (id) => axios.get(`${API_URL}/service-packages/${id}`),
  brands: () => axios.get(`${API_URL}/service-packages/brands`),
  options: () => axios.get(`${API_URL}/service-packages/options`),
  // Resolve the price for a given (brand, fuel, car_type) combo
  quotePrice: (params) => axios.get(`${API_URL}/service-packages/quote-price`, { params }),
  // Curated India makes/models catalogue (cascading dropdowns)
  models: (brand) => axios.get(`${API_URL}/service-packages/models`, { params: brand ? { brand } : {} }),
  // CRM-visible active packages with resolved_price for the supplied vehicle
  eligible: (params) => axios.get(`${API_URL}/service-packages/eligible`, { params }),
};

// Service Ops (Phase 3 — table view + kanban for dispatching mechanics)
export const serviceOpsApi = {
  list: (params) => axios.get(`${API_URL}/service-ops`, { params }),
  counts: (params) => axios.get(`${API_URL}/service-ops/counts`, { params }),
  board: (params) => axios.get(`${API_URL}/service-ops/board`, { params }),
  mechanics: () => axios.get(`${API_URL}/service-ops/mechanics`),
  assignMechanic: (leadId, body) => axios.post(`${API_URL}/service-ops/${leadId}/assign-mechanic`, body),
  updateStatus: (leadId, body) => axios.patch(`${API_URL}/service-ops/${leadId}/status`, body),
  // Single source of truth for statuses + cancellation/refund reason presets.
  statusCatalogue: () => axios.get(`${API_URL}/service-ops/status-catalogue`),
  // Retrospective reason on an already-cancelled job (no status change).
  setClosureReason: (leadId, body) =>
    axios.post(`${API_URL}/service-ops/${leadId}/closure-reason`, body),
  // Rehab (May 2026) ──────────────────────────────────
  completion: (leadId) => axios.get(`${API_URL}/service-ops/${leadId}/completion`),
  // RC lookup → Surepass (authed; uses 24h public_vehicle_cache)
  lookupRc: (regNo) =>
    axios.get(`${API_URL}/service-ops/lookup-rc`, { params: { reg_no: regNo } }),
  listParts: (leadId) => axios.get(`${API_URL}/service-ops/${leadId}/spare-parts`),
  addPart: (leadId, body) => axios.post(`${API_URL}/service-ops/${leadId}/spare-parts`, body),
  // Bulk replace — used by the "Save All Changes" flow on the Pricing & Spares tab.
  replaceParts: (leadId, parts) =>
    axios.put(`${API_URL}/service-ops/${leadId}/spare-parts`, { parts }),
  updatePart: (leadId, partId, body) =>
    axios.patch(`${API_URL}/service-ops/${leadId}/spare-parts/${partId}`, body),
  deletePart: (leadId, partId) =>
    axios.delete(`${API_URL}/service-ops/${leadId}/spare-parts/${partId}`),
  createAdditionalLink: (leadId, body) =>
    axios.post(`${API_URL}/service-ops/${leadId}/additional-payment-link`, body),
  cancelAdditionalLink: (leadId, linkId) =>
    axios.delete(`${API_URL}/service-ops/${leadId}/additional-payment-link/${linkId}`),
  overridePaymentStatus: (leadId, body) =>
    axios.patch(`${API_URL}/service-ops/${leadId}/payment-status`, body),
  generateInvoice: (leadId) =>
    axios.post(`${API_URL}/service-ops/${leadId}/invoice/generate`),
  getInvoice: (leadId) => axios.get(`${API_URL}/service-ops/${leadId}/invoice`),
  invoicePreviewUrl: (leadId, category = "full") =>
    `${API_URL}/service-ops/${leadId}/invoice/preview?category=${category}&_t=${Date.now()}`,
  invoicePdfUrl: (leadId, category = "full") =>
    `${API_URL}/service-ops/${leadId}/invoice/pdf?category=${category}&_t=${Date.now()}`,
  // Fetch the HTML directly (auth header propagates) — used by the iframe
  // srcDoc so we don't hit "Not authenticated" on cross-origin iframe src.
  invoicePreviewHtml: (leadId, category = "full", proforma = false) =>
    axios.get(`${API_URL}/service-ops/${leadId}/invoice/preview`, {
      params: { category, proforma, _t: Date.now() },
      responseType: "text",
      transformResponse: [(d) => d],
    }),
  invoicePdfBlob: (leadId, category = "full", proforma = false) =>
    axios.get(`${API_URL}/service-ops/${leadId}/invoice/pdf`, {
      params: { category, proforma, _t: Date.now() },
      responseType: "blob",
    }),
  sendInvoice: (leadId, body) =>
    axios.post(`${API_URL}/service-ops/${leadId}/invoice/send`, body),
};

// Spare Parts Master (catalogue used by the Ops add-part autocomplete)
export const sparePartsMasterApi = {
  list: (params) => axios.get(`${API_URL}/spare-parts-master`, { params }),
  search: (params) => axios.get(`${API_URL}/spare-parts-master/search`, { params }),
  get: (id) => axios.get(`${API_URL}/spare-parts-master/${id}`),
  create: (body) => axios.post(`${API_URL}/spare-parts-master`, body),
  update: (id, body) => axios.patch(`${API_URL}/spare-parts-master/${id}`, body),
  delete: (id) => axios.delete(`${API_URL}/spare-parts-master/${id}`),
  learn: (body) => axios.post(`${API_URL}/spare-parts-master/learn`, body),
  // Bulk upsert from Service Ops "Save All Changes" — one row per
  // (name+brand+car_make+car_model).
  upsertFromOps: (body) =>
    axios.post(`${API_URL}/spare-parts-master/upsert-from-ops`, body),
  // Brand autocomplete (typeahead) used by the add-part Brand field.
  brands: (params) =>
    axios.get(`${API_URL}/spare-parts-master/brands/autocomplete`, { params }),
};

// Automatic retry for transient failures (network errors, 502/503/504)
axiosRetry(axios, {
  retries: 2,
  retryDelay: (retryCount) => retryCount * 800,
  retryCondition: (error) => {
    // Retry on network errors or server errors (502, 503, 504)
    if (!error.response) return true;
    return [502, 503, 504].includes(error.response.status);
  },
  onRetry: (retryCount, error) => {
    console.log(`[Retry ${retryCount}] ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
  },
});

// Get user's timezone offset in minutes (positive for IST, negative for western timezones)
export const getTimezoneOffset = () => {
  // JavaScript's getTimezoneOffset returns the opposite sign, so we negate it
  // IST (UTC+5:30) returns -330 from getTimezoneOffset(), we need +330 for the backend
  return -new Date().getTimezoneOffset();
};

// Leads API
export const leadsApi = {
  getAll: (params) => axios.get(`${API_URL}/leads`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getById: (id) => axios.get(`${API_URL}/leads/${id}`),
  getStatuses: () => axios.get(`${API_URL}/leads/statuses`),
  create: (data) => axios.post(`${API_URL}/leads`, data),
  update: (id, data) => axios.put(`${API_URL}/leads/${id}`, data),
  updateStatus: (id, status) => axios.patch(`${API_URL}/leads/${id}/status`, { status }),
  delete: (id) => axios.delete(`${API_URL}/leads/${id}`),
  reassign: (id, data) => axios.post(`${API_URL}/leads/${id}/reassign`, data),
  getReassignmentHistory: (id) => axios.get(`${API_URL}/leads/${id}/reassignment-history`),
  // Sales reps for assignment
  getSalesRepsByCity: (city) => axios.get(`${API_URL}/leads/sales-reps-by-city`, { params: { city } }),
  // Bulk assign unassigned leads
  assignUnassigned: () => axios.post(`${API_URL}/leads/assign-unassigned`),
  // Bulk city remap (manual from->to)
  bulkRemapCity: (data) => axios.post(`${API_URL}/leads/bulk-remap-city`, data),
  // Auto remap by AD ID (automatic based on ad_city_mappings)
  autoRemapByAdId: (reassignToSalesRep = true) => axios.post(`${API_URL}/leads/auto-remap-by-ad-id?reassign_to_sales_rep=${reassignToSalesRep}`),
  // City summary
  getCitySummary: () => axios.get(`${API_URL}/leads/city-summary`),
  // Notes
  getNotes: (id) => axios.get(`${API_URL}/leads/${id}/notes`),
  addNote: (id, note) => axios.post(`${API_URL}/leads/${id}/notes`, { note }),
  // Activities
  getActivities: (id) => axios.get(`${API_URL}/leads/${id}/activities`),
  // Inspection progress (read-only; sales roles + CEO) — Inspection Leads page only
  getInspectionProgress: (id) => axios.get(`${API_URL}/leads/${id}/inspection-progress`),
  getInspectionProgressBatch: (ids) =>
    axios.get(`${API_URL}/leads/inspection-progress/batch`, { params: { lead_ids: (ids || []).join(',') } }),
  // Reminder
  setReminder: (id, data) => axios.post(`${API_URL}/leads/${id}/reminder`, data),
  // Payment
  createPaymentLink: (id, data) => axios.post(`${API_URL}/leads/${id}/payment-link`, data),
  // Check payment status manually (Plan B)
  checkPaymentStatus: (id) => axios.get(`${API_URL}/leads/${id}/check-payment-status`),
  // Get payment link history for a lead
  getPaymentLinks: (id) => axios.get(`${API_URL}/leads/${id}/payment-links`),
  // Resend SMS notification via Razorpay
  resendPaymentSms: (id) => axios.post(`${API_URL}/leads/${id}/resend-sms`),
};

// Admin Tools API (admin-only operational helpers)
export const adminApi = {
  // Reconcile a Razorpay payment that succeeded but whose webhook did not
  // complete. Accepts ANY one of: payment_id, payment_link_id, lead_id, phone.
  reconcilePayment: (body) => axios.post(`${API_URL}/admin/payments/reconcile`, body),
  // Fetch the most recent reconcile decisions (cron + manual) for the log widget.
  reconcileLog: (limit = 50) => axios.get(`${API_URL}/admin/payments/reconcile-log`, { params: { limit } }),
  // Payments the centralized sync could NOT resolve to a lead — the review queue.
  unmatchedPayments: (status = 'pending', limit = 200) =>
    axios.get(`${API_URL}/admin/payments/unmatched`, { params: { status, limit } }),
  // Manually attach an unmatched payment to a lead. body: { lead_id, lead_type? }.
  mapUnmatchedPayment: (paymentId, body) =>
    axios.post(`${API_URL}/admin/payments/unmatched/${paymentId}/map`, body),
};

// Dealers API (B2B Vertical)
export const dealersApi = {
  getAll: (params) => axios.get(`${API_URL}/dealers`, { params }),
  getById: (id) => axios.get(`${API_URL}/dealers/${id}`),
  getStatuses: () => axios.get(`${API_URL}/dealers/statuses`),
  getCardCounts: (params) => axios.get(`${API_URL}/dealers/card-counts`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  create: (data) => axios.post(`${API_URL}/dealers`, data),
  update: (id, data) => axios.put(`${API_URL}/dealers/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/dealers/${id}`),
  getDealerTypes: () => axios.get(`${API_URL}/dealers/dealer-types`),
  getActivities: (id) => axios.get(`${API_URL}/dealers/${id}/activities`),
  addActivity: (id, data) => axios.post(`${API_URL}/dealers/${id}/activities`, data),
  bulkImport: (formData) => axios.post(`${API_URL}/dealers/bulk-import`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkAssign: (data) => axios.post(`${API_URL}/dealers/bulk-assign`, data),
  // Packages
  getPackages: (id) => axios.get(`${API_URL}/dealers/${id}/packages`),
  purchasePackage: (id, data) => axios.post(`${API_URL}/dealers/${id}/packages`, data),
  getPackageTemplates: () => axios.get(`${API_URL}/dealers/package-templates/list`),
  createPackageTemplate: (data) => axios.post(`${API_URL}/dealers/package-templates`, data),
  updatePackageTemplate: (id, data) => axios.put(`${API_URL}/dealers/package-templates/${id}`, data),
  deletePackageTemplate: (id) => axios.delete(`${API_URL}/dealers/package-templates/${id}`),
  // Inspections
  getDealerInspections: (params) => axios.get(`${API_URL}/dealers/inspections/list`, { params }),
  createDealerInspection: (dealerId, data) => axios.post(`${API_URL}/dealers/${dealerId}/inspections`, data),
  // Payment link
  sendPaymentLink: (dealerId, data) => axios.post(`${API_URL}/dealers/${dealerId}/send-payment-link`, data),
  createRazorpayOrder: (dealerId, data) => axios.post(`${API_URL}/dealers/${dealerId}/create-razorpay-order`, data),
  verifyPayment: (dealerId, data) => axios.post(`${API_URL}/dealers/${dealerId}/verify-payment`, data),
  // Sample template - download via authenticated request
  downloadSampleTemplate: () => axios.get(`${API_URL}/dealers/sample-import-template`, { responseType: 'blob' }),
  getAssignableEmployees: () => axios.get(`${API_URL}/dealers/assignable-employees`),
};

// Vehicle API (Vaahan Integration)
export const vehicleApi = {
  // Fetch vehicle details — cache-first. forceRefresh costs a paid RC call.
  getDetails: (vehicleNumber, { forceRefresh = false, identityOnly = false } = {}) =>
    axios.get(`${API_URL}/vehicle/details/${encodeURIComponent(vehicleNumber)}`,
      { params: {
          ...(forceRefresh ? { force_refresh: true } : {}),
          ...(identityOnly ? { identity_only: true } : {}),
        } }),
  // Save vehicle to database
  save: (data) => axios.post(`${API_URL}/vehicles`, data),
  // Get vehicle by ID
  getById: (id) => axios.get(`${API_URL}/vehicles/${id}`),
  // Get vehicle by registration number
  getByRegistration: (regNumber) => axios.get(`${API_URL}/vehicles/by-registration/${encodeURIComponent(regNumber)}`),
};

// Ad-City Mappings API
export const adCityMappingsApi = {
  getAll: () => axios.get(`${API_URL}/settings/ad-city-mappings`),
  create: (data) => axios.post(`${API_URL}/settings/ad-city-mappings`, data),
  update: (id, data) => axios.put(`${API_URL}/settings/ad-city-mappings/${id}`, data),
  toggleStatus: (id) => axios.patch(`${API_URL}/settings/ad-city-mappings/${id}/toggle-status`),
  delete: (id) => axios.delete(`${API_URL}/settings/ad-city-mappings/${id}`),
};

// Meta Ads Analytics API
// Google Ads × Services — the "Services" channel of Ads Management. Spend comes from the
// Google Ads API snapshot; money follows the CEO's payment-received clock.
export const googleAdsApi = {
  getPerformance: (params) => axios.get(`${API_URL}/google-ads/performance`, { params }),
  // Car-service demand from cities we are not live in yet (read-only).
  getUnservicedDemand: (params) => axios.get(`${API_URL}/google-ads/unserviced-demand`, { params }),
  // The same demand on a map of India: one bubble per city, then dots per customer.
  getDemandMap: (params) => axios.get(`${API_URL}/google-ads/demand-map`, { params }),
  getCityPoints: (params) => axios.get(`${API_URL}/google-ads/demand-map/city`, { params }),
  getSyncStatus: () => axios.get(`${API_URL}/google-ads/sync-status`),
  getSyncJob: () => axios.get(`${API_URL}/google-ads/sync-job`),
  // Starts a background sync for the window on screen and returns immediately —
  // a synchronous 30-day pull always died on the 120s proxy timeout.
  sync: (params = {}) => axios.post(`${API_URL}/google-ads/sync`, null, { params }),
  backfillClicks: (days = 90) => axios.post(`${API_URL}/google-ads/backfill-clicks`, null, { params: { days } }),
};

// Is Google actually RECEIVING what we earned? Two separate pipelines:
//   • offline-conversions — Booked/Paid pushed back by click id (Data Manager API)
//   • conversion-adjustments — restating the website's hardcoded ₹1 lead value
// Finance-gated, so a 403 here just means "hide the card".
export const conversionDeliveryApi = {
  getOfflineStatus: () => axios.get(`${API_URL}/dashboard/services/offline-conversions/status`),
  getAdjustmentStatus: () => axios.get(`${API_URL}/dashboard/services/conversion-adjustments/status`),
  runOfflineSweep: () => axios.post(`${API_URL}/dashboard/services/offline-conversions/run-sweep`),

  // WhatsApp "chat started" conversions — a real inbound chat from a Google click,
  // posted to SocialOrange for upload to Google Ads. Founder-only (403 otherwise).
  getWhatsAppChatConversionStatus: () =>
    axios.get(`${API_URL}/dashboard/services/whatsapp-chat-conversions/status`),
  runWhatsAppChatConversionSweep: () =>
    axios.post(`${API_URL}/dashboard/services/whatsapp-chat-conversions/run-sweep`),
  runAdjustmentSweep: (validateOnly) => axios.post(
    `${API_URL}/dashboard/services/conversion-adjustments/run-sweep`, null,
    { params: validateOnly === undefined ? {} : { validate_only: validateOnly } }),
};


export const metaAdsApi = {
  getStatus: () => axios.get(`${API_URL}/meta-ads/status`),
  getTokenInfo: () => axios.get(`${API_URL}/meta-ads/token-info`),
  refreshToken: () => axios.post(`${API_URL}/meta-ads/refresh-token`),
  autoRefresh: (daysThreshold = 7) => axios.post(`${API_URL}/meta-ads/auto-refresh`, null, { params: { days_threshold: daysThreshold } }),
  updateToken: (accessToken) => axios.post(`${API_URL}/meta-ads/update-token`, { access_token: accessToken }),
  getInsights: (params) => axios.get(`${API_URL}/meta-ads/insights`, { params }),
  getCampaigns: () => axios.get(`${API_URL}/meta-ads/campaigns`),
  getAds: () => axios.get(`${API_URL}/meta-ads/ads`),
  getPerformance: (params) => axios.get(`${API_URL}/meta-ads/performance`, { params }),
  // "It worked for the last N days and now it doesn't" — compares the last N days
  // with the N before and names the funnel stage that moved.
  getDiagnosis: (params) => axios.get(`${API_URL}/meta-ads/diagnosis`, { params }),
  // OAuth endpoints
  initOAuth: () => axios.get(`${API_URL}/meta-ads/oauth/init`),
  handleOAuthCallback: (code) => axios.post(`${API_URL}/meta-ads/oauth/callback`, null, { params: { code } }),
  // Advanced features - sync and unmapped ads
  syncStatus: () => axios.post(`${API_URL}/meta-ads/sync-status`),
  forceSync: () => axios.post(`${API_URL}/meta-ads/force-sync`),
  getCachedPerformance: () => axios.get(`${API_URL}/meta-ads/cached-performance`),
  getUnmappedAds: () => axios.get(`${API_URL}/meta-ads/unmapped-ads`),
  getAdsWithTargeting: () => axios.get(`${API_URL}/meta-ads/ads-with-targeting`),
  // Auto-map from targeting
  autoMapFromTargeting: () => axios.post(`${API_URL}/meta-ads/auto-map-from-targeting`),
  // ONE-CLICK SYNC: Fetch all Meta ads and create comprehensive ad_name -> city mappings
  syncAllAdMappings: () => axios.post(`${API_URL}/meta-ads/sync-all-ad-mappings`),
  // Auto-map from current cities (based on lead data)
  autoMapFromCurrentCities: () => axios.post(`${API_URL}/meta-ads/auto-map-from-current-cities`),
  // Scan leads for unmapped ads (works without Meta token)
  scanLeadsForUnmappedAds: () => axios.post(`${API_URL}/meta-ads/scan-leads-for-unmapped-ads`),
  // Unmapped ads from WhatsApp leads (works without Meta token)
  getUnmappedAdsFromLeads: () => axios.get(`${API_URL}/meta-ads/unmapped-ads-from-leads`),
  mapAdFromLeads: (unmappedId, city) => axios.post(`${API_URL}/meta-ads/map-ad-from-leads/${unmappedId}?city=${encodeURIComponent(city)}`),
};

// Meta Conversions API (CAPI) - Server-side conversion tracking
export const metaCAPIApi = {
  // Get CAPI status and configuration
  getStatus: () => axios.get(`${API_URL}/meta-capi/status`),
  // Test CAPI connection
  testConnection: (testEventCode = null) => axios.post(`${API_URL}/meta-capi/test`, { test_event_code: testEventCode }),
  // Send manual conversion event
  sendEvent: (eventType, leadId = null, inspectionId = null, paymentId = null, amount = null) => 
    axios.post(`${API_URL}/meta-capi/send-event`, { 
      event_type: eventType, 
      lead_id: leadId, 
      inspection_id: inspectionId, 
      payment_id: paymentId, 
      amount: amount 
    }),
  // Get conversion statistics
  getConversionStats: (days = 30) => axios.get(`${API_URL}/meta-capi/conversion-stats`, { params: { days } }),
};

// Customers API
export const customersApi = {
  getAll: (params) => axios.get(`${API_URL}/customers`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getById: (id) => axios.get(`${API_URL}/customers/${id}`),
  create: (data) => axios.post(`${API_URL}/customers`, data),
  update: (id, data) => axios.put(`${API_URL}/customers/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/customers/${id}`),
  // Payment history
  getPaymentHistory: (id) => axios.get(`${API_URL}/customers/${id}/payment-history`),
  getDetailedPayments: (id) => axios.get(`${API_URL}/customers/${id}/detailed-payments`),
  // Notes & Activities
  getNotes: (id) => axios.get(`${API_URL}/customers/${id}/notes`),
  addNote: (id, note) => axios.post(`${API_URL}/customers/${id}/notes`, { note }),
  updateNote: (customerId, noteId, note) => axios.put(`${API_URL}/customers/${customerId}/notes/${noteId}`, { note }),
  deleteNote: (customerId, noteId) => axios.delete(`${API_URL}/customers/${customerId}/notes/${noteId}`),
  getActivities: (id) => axios.get(`${API_URL}/customers/${id}/activities`),
  // Sales reps with counts
  getSalesRepsWithCounts: () => axios.get(`${API_URL}/customers/sales-reps-with-counts`),
  getCardCounts: (params) => axios.get(`${API_URL}/customers/card-counts`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  // Seed sample data
  seedSampleData: () => axios.post(`${API_URL}/customers/seed-sample-data`),
  // Repair data - link orphaned inspections, create missing inspections
  repair: (id) => axios.post(`${API_URL}/customers/${id}/repair`),
  repairByMobile: (mobile) => axios.post(`${API_URL}/customers/repair-by-mobile/${encodeURIComponent(mobile)}`),
  // Service Customers (added 2026-06-04) — service_leads who have transacted (PARTIAL_PAID/PAID/COMPLETED).
  getServiceCustomers: (params) =>
    axios.get(`${API_URL}/customers/service`, {
      params: { ...params, timezone_offset: getTimezoneOffset() },
    }),
  getServiceCustomerCardCounts: (params) =>
    axios.get(`${API_URL}/customers/service/card-counts`, {
      params: { ...params, timezone_offset: getTimezoneOffset() },
    }),
  getServiceCustomerDetail: (id) =>
    axios.get(`${API_URL}/customers/service/${id}`),
};

// Transactions API
export const transactionsApi = {
  getByCustomer: (customerId) => axios.get(`${API_URL}/transactions/${customerId}`),
  create: (data) => axios.post(`${API_URL}/transactions`, data),
};

// Inspections API
export const inspectionsApi = {
  getAll: (params) => axios.get(`${API_URL}/inspections`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getCounts: (params) => axios.get(`${API_URL}/inspections/counts`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getCardCounts: (params) => axios.get(`${API_URL}/inspections/card-counts`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getById: (id) => axios.get(`${API_URL}/inspections/${id}`),
  create: (data) => axios.post(`${API_URL}/inspections`, data),
  update: (id, data) => axios.put(`${API_URL}/inspections/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/inspections/${id}`),
  collectBalance: (id, data) => axios.post(`${API_URL}/inspections/${id}/collect-balance`, data),
  checkPaymentStatus: (id, linkId) => axios.get(`${API_URL}/inspections/${id}/check-payment-status`, { params: { link_id: linkId } }),
  updateStatus: (id, status, reason = null) => axios.patch(`${API_URL}/inspections/${id}/status`, null, { params: { inspection_status: status, reason: reason } }),
  sendReport: (id, data) => axios.post(`${API_URL}/inspections/${id}/send-report`, data),
  updateVehicle: (id, data) => axios.patch(`${API_URL}/inspections/${id}/vehicle`, data),
  assignMechanic: (id, mechanicId) => axios.patch(`${API_URL}/inspections/${id}/assign-mechanic`, { mechanic_id: mechanicId }),
  updateSchedule: (id, data) => axios.patch(`${API_URL}/inspections/${id}/schedule`, data),
  updateLocation: (id, data) => axios.patch(`${API_URL}/inspections/${id}/location`, data),
  getLiveProgress: (id) => axios.get(`${API_URL}/inspections/${id}/live-progress`),
  toggleOBDRescan: (id, enableRescan) => axios.post(`${API_URL}/inspections/${id}/obd-rescan`, { enable_rescan: enableRescan }),
  requestObdRescan: (id) => axios.post(`${API_URL}/inspections/${id}/obd-rescan`, { enable_rescan: true }),
  // Vaahan API integration. forceRefresh=true bypasses the db.vehicles cache
  // AND the negative-cache suppression window — it costs a paid provider call,
  // so only ever wire it to an explicit "Refresh RC" click.
  fetchVaahanData: (id, { forceRefresh = false } = {}) =>
    axios.post(`${API_URL}/inspections/${id}/fetch-vaahan-data${forceRefresh ? '?force_refresh=true' : ''}`),
  // KM Extraction from Dashboard Photo
  extractKMFromPhoto: (id, imageUrl) => axios.post(`${API_URL}/inspections/${id}/extract-km-from-photo`, { image_url: imageUrl }),
  // Challan Data
  fetchChallans: (id) => axios.post(`${API_URL}/inspections/${id}/fetch-challans`),
  // Manual KM Reading Update
  updateKmReading: (id, data) => axios.put(`${API_URL}/inspections/${id}/km-reading`, data),
  // Notes & Activities
  getNotes: (id) => axios.get(`${API_URL}/inspections/${id}/notes`),
  addNote: (id, data) => axios.post(`${API_URL}/inspections/${id}/notes`, data),
  getActivities: (id) => axios.get(`${API_URL}/inspections/${id}/activities`),
  // Answer Editing (CRM)
  editAnswer: (id, questionId, data) => axios.put(`${API_URL}/inspections/${id}/answers/${questionId}`, data),
  uploadQuestionMedia: (id, questionId, formData, onProgress) => axios.post(
    `${API_URL}/inspections/${id}/questions/${questionId}/upload-media`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: onProgress }
  ),
  getAnswerHistory: (id, questionId) => axios.get(`${API_URL}/inspections/${id}/answers/${questionId}/history`),
  getEditHistory: (id) => axios.get(`${API_URL}/inspections/${id}/edit-history`),
  // AI Report Generation
  generateAIReport: (id, forceRegenerate = false) => axios.post(`${API_URL}/inspections/${id}/generate-ai-report`, { force_regenerate: forceRegenerate }),
  getReport: (id) => axios.get(`${API_URL}/inspections/${id}/report`),
  // Recommended Purchase Price (RPP) - Market Price Research
  fetchRPP: (id) => axios.post(`${API_URL}/inspections/${id}/fetch-rpp`),
  // Report URL (short URL for customer access)
  getShortUrl: (id) => axios.get(`${API_URL}/inspections/${id}/short-url`),
  // Publish Report APIs
  publishReport: (id, data) => axios.post(`${API_URL}/inspections/${id}/publish-report`, data),
  getPublishHistory: (id) => axios.get(`${API_URL}/inspections/${id}/publish-history`),
  getCustomerPortalLink: (id) => axios.get(`${API_URL}/inspections/${id}/customer-portal-link`),
  getPortalAudit: (customerId) => axios.get(`${API_URL}/customer-portal/audit/${customerId}`),
  // Calculated Repairs for Report
  getCalculatedRepairs: (id) => axios.get(`${API_URL}/inspections/${id}/calculated-repairs`),
  // Portal OTP Management
  sendPortalOtp: (id) => axios.post(`${API_URL}/inspections/${id}/send-portal-otp`),
  resendPortalOtp: (id) => axios.post(`${API_URL}/inspections/${id}/resend-portal-otp`),
  getPortalOtpLogs: (id) => axios.get(`${API_URL}/inspections/${id}/portal-otp-logs`),
  // Download Report as PDF (longer timeout for image embedding)
  downloadReportPdf: (id, brand = 'wisedrive') => axios.get(`${API_URL}/inspections/${id}/report-pdf?brand=${brand}`, { responseType: 'blob', timeout: 120000 }),
  // Merge Duplicates
  detectDuplicates: () => axios.get(`${API_URL}/inspections/detect-duplicates`),
  mergeDuplicates: (leadId, keepIds) => axios.post(`${API_URL}/inspections/merge-duplicates/${leadId}`, { keep_ids: keepIds }),
};

// Users API (V2)
export const usersApi = {
  getAll: (params) => axios.get(`${API_URL}/users`, { params }),
  getById: (id) => axios.get(`${API_URL}/users/${id}`),
  toggleStatus: (id) => axios.patch(`${API_URL}/users/${id}/toggle-status`),
  toggleAssignment: (id) => axios.patch(`${API_URL}/users/${id}/toggle-assignment`),
};

// Employees API (V1 compatibility)
export const employeesApi = {
  getAll: () => axios.get(`${API_URL}/employees`),
  create: (data) => axios.post(`${API_URL}/employees`, data),
  update: (id, data) => axios.put(`${API_URL}/employees/${id}`, data),
  toggleStatus: (id) => axios.patch(`${API_URL}/employees/${id}/toggle-status`),
  assignCity: (id, city) => axios.patch(`${API_URL}/employees/${id}/assign-city`, null, { params: { city } }),
  getSalesForAssignment: () => axios.get(`${API_URL}/sales-employees-for-assignment`),
};

// Mechanics API
export const mechanicsApi = {
  getAll: (params) => axios.get(`${API_URL}/mechanics`, { params }),
};

// Countries API
export const countriesApi = {
  getAll: () => axios.get(`${API_URL}/countries`),
  getById: (id) => axios.get(`${API_URL}/countries/${id}`),
};

// Departments API
export const departmentsApi = {
  getAll: () => axios.get(`${API_URL}/departments`),
};

// Roles API
export const rolesApi = {
  getAll: () => axios.get(`${API_URL}/roles`),
  getPermissions: (id) => axios.get(`${API_URL}/roles/${id}/permissions`),
  create: (data) => axios.post(`${API_URL}/roles`, data),
  update: (id, data) => axios.put(`${API_URL}/roles/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/roles/${id}`),
};

// Teams API
export const teamsApi = {
  getAll: (params) => axios.get(`${API_URL}/teams`, { params }),
};

// Round Robin API
export const roundRobinApi = {
  getNextAgent: (countryId, teamId) => axios.get(`${API_URL}/round-robin/next/${countryId}`, { params: { team_id: teamId } }),
  getStats: (countryId, teamId) => axios.get(`${API_URL}/round-robin/stats/${countryId}`, { params: { team_id: teamId } }),
};

// Digital Ads API
export const digitalAdsApi = {
  getAll: () => axios.get(`${API_URL}/digital-ads`),
  create: (data) => axios.post(`${API_URL}/digital-ads`, data),
  update: (id, data) => axios.put(`${API_URL}/digital-ads/${id}`, data),
  toggleStatus: (id) => axios.patch(`${API_URL}/digital-ads/${id}/toggle-status`),
  delete: (id) => axios.delete(`${API_URL}/digital-ads/${id}`),
};

// Garage Employees API
export const garageEmployeesApi = {
  getAll: () => axios.get(`${API_URL}/garage-employees`),
  create: (data) => axios.post(`${API_URL}/garage-employees`, data),
  update: (id, data) => axios.put(`${API_URL}/garage-employees/${id}`, data),
  toggleStatus: (id) => axios.patch(`${API_URL}/garage-employees/${id}/toggle-status`),
  delete: (id) => axios.delete(`${API_URL}/garage-employees/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => axios.get(`${API_URL}/dashboard/stats`),
  getAnalytics: (params) => axios.get(`${API_URL}/dashboard/analytics`, { params }),
  getTrends: (params) => axios.get(`${API_URL}/dashboard/trends`, { params }),
  getComparison: (params) => axios.get(`${API_URL}/dashboard/comparison`, { params }),
  getCityAnalysis: (cityName, params) => axios.get(`${API_URL}/dashboard/city-analysis/${encodeURIComponent(cityName)}`, { params }),
  getEmployeeAnalysis: (employeeId, params) => axios.get(`${API_URL}/dashboard/employee-analysis/${encodeURIComponent(employeeId)}`, { params }),
  getAdsAnalysis: () => axios.get(`${API_URL}/dashboard/ads-analysis`),
  getAiInsights: (params) => axios.post(`${API_URL}/dashboard/ai-insights`, null, { params }),
  aiQuery: (data) => axios.post(`${API_URL}/dashboard/ai-query`, data),
  getAdvancedMetrics: (params) => axios.get(`${API_URL}/dashboard/advanced-metrics`, { params }),
  getLeadJourney: (params) => axios.get(`${API_URL}/dashboard/lead-journey`, { params }),
  getTodayDashboard: (params) => axios.get(`${API_URL}/dashboard/today`, { params }),
  getLeadsByStatusCounts: (params) => axios.get(`${API_URL}/dashboard/today/leads-by-status`, { params }),
  getLeadsByStatusSection: (params) => axios.get(`${API_URL}/dashboard/today/leads-by-status-section`, { params }),
  getStatusAging: (params) => axios.get(`${API_URL}/dashboard/today/status-aging`, { params }),
  getPaymentsBreakdown: (params) => axios.get(`${API_URL}/dashboard/payments-breakdown`, { params }),
};

// Analytics API
export const analyticsApi = {
  getConversionIntelligence: (params) => axios.get(`${API_URL}/analytics/conversion-intelligence`, { params }),
  getCampaignDrilldown: (params) => axios.get(`${API_URL}/analytics/campaign-drilldown`, { params }),
  getEmployeeDrilldown: (params) => axios.get(`${API_URL}/analytics/employee-drilldown`, { params }),
};

// Utility API
export const utilityApi = {
  getCities: () => axios.get(`${API_URL}/cities/names`),
  getLeadSources: () => axios.get(`${API_URL}/lead-sources`),
  getLeadStatuses: () => axios.get(`${API_URL}/leads/statuses`),
  getLeadStatusCounts: (params) => axios.get(`${API_URL}/leads/status-counts`, { params }),
  getLeadCityCounts: (params) => axios.get(`${API_URL}/leads/city-counts`, { params }),
  getLeadEmployeeCounts: (params) => axios.get(`${API_URL}/leads/employee-counts`, { params }),
  getLeadCardCounts: (params) => axios.get(`${API_URL}/leads/card-counts`, { params }),
};

// Cities Management API
//
// Both endpoints dedupe responses by (name, country_id) on the client so
// duplicate rows in the DB (legacy artefact from a pre-2026-05-15 auto-seed
// race) never reach the UI. The backend admin endpoint
// /api/admin/dedupe-cities cleans the DB permanently and a unique index
// prevents recurrence, but this client-side dedupe is the immediate
// guarantee that no UI ever shows duplicates regardless of DB state.
const _dedupeCities = (resp) => {
  if (!resp || !Array.isArray(resp.data)) return resp;
  const seen = new Set();
  const unique = [];
  for (const c of resp.data) {
    if (!c) continue;
    // dedupe key: lowercased name + country_id (handles case-variant duplicates)
    const key = `${(c.name || '').toLowerCase()}|${c.country_id || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(c);
  }
  return { ...resp, data: unique };
};

const _dedupeCityNames = (resp) => {
  if (!resp || !Array.isArray(resp.data)) return resp;
  return { ...resp, data: Array.from(new Set(resp.data)) };
};

export const citiesApi = {
  getAll: (includeInactive = true) =>
    axios.get(`${API_URL}/cities`, { params: { include_inactive: includeInactive } })
         .then(_dedupeCities),
  getNames: () =>
    axios.get(`${API_URL}/cities/names`).then(_dedupeCityNames),
  create: (data) => axios.post(`${API_URL}/cities`, data),  // JSON body for proper array handling
  update: (id, data) => axios.put(`${API_URL}/cities/${id}`, data),  // JSON body for proper array handling
  resolve: (cityName) => axios.get(`${API_URL}/cities/resolve/${encodeURIComponent(cityName)}`),
  normalizeAll: () => axios.post(`${API_URL}/cities/normalize-all`),
};

// utilityApi.getCities also dedupes for consumers that import it.
const _origUtilGetCities = utilityApi.getCities;
utilityApi.getCities = (...args) => _origUtilGetCities(...args).then(_dedupeCityNames);

// Seed API
export const seedApi = {
  seed: () => axios.post(`${API_URL}/seed`),
  clearAndSeed: () => axios.post(`${API_URL}/seed/clear`),
};

// Inspection Q&A API
export const inspectionQAApi = {
  getQuestions: (params) => axios.get(`${API_URL}/inspection-qa/questions`, { params }),
  getQuestion: (id) => axios.get(`${API_URL}/inspection-qa/questions/${id}`),
  createQuestion: (data) => axios.post(`${API_URL}/inspection-qa/questions`, data),
  updateQuestion: (id, data) => axios.put(`${API_URL}/inspection-qa/questions/${id}`, data),
  deleteQuestion: (id) => axios.delete(`${API_URL}/inspection-qa/questions/${id}`),
  toggleQuestion: (id) => axios.patch(`${API_URL}/inspection-qa/questions/${id}/toggle`),
  getCategories: () => axios.get(`${API_URL}/inspection-qa/categories`),
  reorderQuestions: (questionIds) => axios.patch(`${API_URL}/inspection-qa/questions/reorder`, { question_ids: questionIds }),
};

// Partners API
export const partnersApi = {
  getPartners: (params) => axios.get(`${API_URL}/partners`, { params }),
  getPartner: (id) => axios.get(`${API_URL}/partners/${id}`),
  createPartner: (data) => axios.post(`${API_URL}/partners`, data),
  updatePartner: (id, data) => axios.put(`${API_URL}/partners/${id}`, data),
  deletePartner: (id) => axios.delete(`${API_URL}/partners/${id}`),
  togglePartner: (id) => axios.patch(`${API_URL}/partners/${id}/toggle`),
};

// Inspection Templates API
export const inspectionTemplatesApi = {
  getTemplates: (params) => axios.get(`${API_URL}/inspection-templates`, { params }),
  getTemplate: (id) => axios.get(`${API_URL}/inspection-templates/${id}`),
  createTemplate: (data) => axios.post(`${API_URL}/inspection-templates`, data),
  updateTemplate: (id, data) => axios.put(`${API_URL}/inspection-templates/${id}`, data),
  deleteTemplate: (id) => axios.delete(`${API_URL}/inspection-templates/${id}`),
  toggleTemplate: (id) => axios.patch(`${API_URL}/inspection-templates/${id}/toggle`),
  setDefault: (id) => axios.patch(`${API_URL}/inspection-templates/${id}/set-default`),
  seedDefault: () => axios.post(`${API_URL}/inspection-templates/seed-default`),
};

// Report Templates API
export const reportTemplatesApi = {
  getTemplates: (params) => axios.get(`${API_URL}/report-templates`, { params }),
  getTemplate: (id) => axios.get(`${API_URL}/report-templates/${id}`),
  createTemplate: (data) => axios.post(`${API_URL}/report-templates`, data),
  updateTemplate: (id, data) => axios.put(`${API_URL}/report-templates/${id}`, data),
  deleteTemplate: (id) => axios.delete(`${API_URL}/report-templates/${id}`),
  toggleTemplate: (id) => axios.patch(`${API_URL}/report-templates/${id}/toggle`),
  setDefault: (id) => axios.patch(`${API_URL}/report-templates/${id}/set-default`),
  getStyles: () => axios.get(`${API_URL}/report-templates/styles`),
  seedSamples: () => axios.post(`${API_URL}/report-templates/seed-samples`),
  getByPartner: (partnerId) => axios.get(`${API_URL}/report-templates/by-partner/${partnerId}`),
};

// Comprehensive HR API
export const hrApi = {
  // Dashboard Stats
  getDashboardStats: (countryId) => axios.get(`${API_URL}/hr/dashboard-stats`, { params: { country_id: countryId } }),
  getEmployeesOnLeaveToday: (countryId) => axios.get(`${API_URL}/hr/employees/on-leave-today`, { params: { country_id: countryId } }),
  
  // Employees
  getEmployees: (params) => axios.get(`${API_URL}/hr/employees`, { params }),
  getEmployee: (id) => axios.get(`${API_URL}/hr/employees/${id}`),
  createEmployee: (data) => axios.post(`${API_URL}/hr/employees`, data),
  updateEmployee: (id, data) => axios.put(`${API_URL}/hr/employees/${id}`, data),
  deleteEmployee: (id) => axios.delete(`${API_URL}/hr/employees/${id}`),
  
  // Password Management
  resetPassword: (id, newPassword) => axios.post(`${API_URL}/hr/employees/${id}/reset-password`, { new_password: newPassword }),
  
  // Salary Structure
  getEmployeeSalary: (id) => axios.get(`${API_URL}/hr/employees/${id}/salary`),
  saveEmployeeSalary: (id, data) => axios.post(`${API_URL}/hr/employees/${id}/salary`, data),
  
  // Salary Payments (History)
  getSalaryPayments: (id, params) => axios.get(`${API_URL}/hr/employees/${id}/salary-payments`, { params }),
  saveSalaryPayment: (id, data) => axios.post(`${API_URL}/hr/employees/${id}/salary-payments`, data),
  
  // Leave Summary
  getLeaveSummary: (id, params) => axios.get(`${API_URL}/hr/employees/${id}/leave-summary`, { params }),
  
  // Attendance
  getEmployeeAttendance: (id, params) => axios.get(`${API_URL}/hr/employees/${id}/attendance`, { params }),
  saveEmployeeAttendance: (id, data) => axios.post(`${API_URL}/hr/employees/${id}/attendance`, data),
  markAttendance: (data) => axios.post(`${API_URL}/hr/attendance/mark`, data),
  
  // Documents
  getEmployeeDocuments: (id) => axios.get(`${API_URL}/hr/employees/${id}/documents`),
  addEmployeeDocument: (id, data) => axios.post(`${API_URL}/hr/employees/${id}/documents`, data),
  uploadEmployeeDocument: (id, formData) => axios.post(`${API_URL}/hr/employees/${id}/documents/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadEmployeeDocument: (id, filename) => axios.get(`${API_URL}/hr/employees/${id}/documents/file/${filename}`, {
    responseType: 'blob'
  }),
  verifyEmployeeDocument: (id, docId) => axios.put(`${API_URL}/hr/employees/${id}/documents/${docId}/verify`),
  updateEmployeeDocument: (id, docId, data) => axios.put(`${API_URL}/hr/employees/${id}/documents/${docId}`, data),
  deleteEmployeeDocument: (id, docId) => axios.delete(`${API_URL}/hr/employees/${id}/documents/${docId}`),
  
  // Audit
  getEmployeeAudit: (id) => axios.get(`${API_URL}/hr/employees/${id}/audit`),
  
  // Lead Assignment Control
  toggleLeadAssignment: (id, data) => axios.patch(`${API_URL}/hr/employees/${id}/lead-assignment`, data),
  updateWeeklyOff: (id, data) => axios.patch(`${API_URL}/hr/employees/${id}/weekly-off`, data),
  
  // Assigned Cities (for Sales reps)
  getAssignedCities: (id) => axios.get(`${API_URL}/hr/employees/${id}/assigned-cities`),
  updateAssignedCities: (id, cities) => axios.put(`${API_URL}/hr/employees/${id}/assigned-cities`, { employee_id: id, cities }),
  
  // Inspection Cities (for Mechanics)
  getInspectionCities: (id) => axios.get(`${API_URL}/hr/employees/${id}/inspection-cities`),
  updateInspectionCities: (id, cities) => axios.put(`${API_URL}/hr/employees/${id}/inspection-cities`, { employee_id: id, cities }),
  // Service-leads vertical city allocation (distinct from inspection cities)
  updateServiceCities: (id, cities) => axios.put(`${API_URL}/hr/employees/${id}/service-cities`, { cities }),
  updateLoanCities: (id, cities) => axios.put(`${API_URL}/hr/employees/${id}/loan-cities`, { cities }),
  getLoanCities: (id) => axios.get(`${API_URL}/hr/employees/${id}/loan-cities`),
  getServiceCities: (id) => axios.get(`${API_URL}/hr/employees/${id}/service-cities`),
  
  // Countries
  getCountries: () => axios.get(`${API_URL}/hr/countries`),
  getAllCountries: () => axios.get(`${API_URL}/hr/countries/all`),
  createCountry: (data) => axios.post(`${API_URL}/hr/countries`, data),
  updateCountry: (id, data) => axios.put(`${API_URL}/hr/countries/${id}`, data),
  deleteCountry: (id) => axios.delete(`${API_URL}/hr/countries/${id}`),
};

// Public Countries API (for login)
export const publicApi = {
  getLoginCountries: () => axios.get(`${API_URL}/countries/login`),
};

// Leave Rules API
export const leaveRulesApi = {
  get: () => axios.get(`${API_URL}/leave-rules`),
  update: (data) => axios.put(`${API_URL}/leave-rules`, data),
};

// Legacy Salary/HR API (for backward compatibility)
export const salaryApi = {
  getAll: (params) => axios.get(`${API_URL}/salaries`, { params }),
  getByUser: (userId) => axios.get(`${API_URL}/salaries/${userId}`),
  create: (data) => axios.post(`${API_URL}/salaries`, data),
  update: (id, data) => axios.put(`${API_URL}/salaries/${id}`, data),
};

// Audit Logs API
export const auditLogsApi = {
  getAll: (params) => axios.get(`${API_URL}/audit-logs`, { params }),
  getByEntity: (entityType, entityId) => axios.get(`${API_URL}/audit-logs/entity/${entityType}/${entityId}`),
  getStats: () => axios.get(`${API_URL}/audit-logs/stats`),
};

// Finance API
export const financeApi = {
  // Payments
  getPayments: (params) => axios.get(`${API_URL}/finance/payments`, { params }),
  getPayment: (id) => axios.get(`${API_URL}/finance/payments/${id}`),
  createPayment: (data) => axios.post(`${API_URL}/finance/payments`, data),
  updatePayment: (id, data) => axios.put(`${API_URL}/finance/payments/${id}`, data),
  deletePayment: (id) => axios.delete(`${API_URL}/finance/payments/${id}`),
  
  // Workflow
  submitForApproval: (id) => axios.patch(`${API_URL}/finance/payments/${id}/submit`),
  approvePayment: (id, data) => axios.patch(`${API_URL}/finance/payments/${id}/approve`, data),
  markAsPaid: (id, params) => axios.patch(`${API_URL}/finance/payments/${id}/mark-paid`, null, { params }),
  
  // Proofs
  getProofs: (paymentId) => axios.get(`${API_URL}/finance/payments/${paymentId}/proofs`),
  addProof: (paymentId, params) => axios.post(`${API_URL}/finance/payments/${paymentId}/proofs`, null, { params }),
  deleteProof: (paymentId, proofId) => axios.delete(`${API_URL}/finance/payments/${paymentId}/proofs/${proofId}`),
  
  // Payslip
  getPayslip: (paymentId) => axios.get(`${API_URL}/finance/payments/${paymentId}/payslip`),
  
  // Summary
  getSummary: (params) => axios.get(`${API_URL}/finance/summary`, { params }),
  
  // Employees for payment
  getEmployees: (params) => axios.get(`${API_URL}/finance/employees`, { params }),
  
  // Payment modes
  getPaymentModes: () => axios.get(`${API_URL}/finance/payment-modes`),
};

// ==================== HOLIDAY CALENDAR APIs ====================

export const holidayApi = {
  getAll: (params) => axios.get(`${API_URL}/hr/holidays`, { params }),
  create: (data) => axios.post(`${API_URL}/hr/holidays`, data),
  update: (id, data) => axios.put(`${API_URL}/hr/holidays/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/hr/holidays/${id}`),
};

// ==================== HR MODULE APIs ====================

export const attendanceApi = {
  // Session management
  startSession: () => axios.post(`${API_URL}/hr/session/start`),
  heartbeat: () => axios.post(`${API_URL}/hr/session/heartbeat`),
  endSession: () => axios.post(`${API_URL}/hr/session/end`),
  
  // Active sessions (HR view)
  getActiveSessions: (countryId) => axios.get(`${API_URL}/hr/sessions/active`, { params: { country_id: countryId } }),
  forceLogout: (sessionId) => axios.post(`${API_URL}/hr/sessions/${sessionId}/force-logout`),
  
  // Attendance records
  getAttendance: (params) => axios.get(`${API_URL}/hr/attendance`, { params }),
  getAttendanceSummary: (employeeId, month, year) => axios.get(`${API_URL}/hr/attendance/summary/${employeeId}`, { params: { month, year } }),
  getPendingApprovals: (countryId) => axios.get(`${API_URL}/hr/attendance/pending-approvals`, { params: { country_id: countryId } }),
  overrideAttendance: (recordId, data) => axios.post(`${API_URL}/hr/attendance/${recordId}/override`, data),
  calculateDaily: (date) => axios.post(`${API_URL}/hr/attendance/calculate-daily`, null, { params: { date } }),
  
  // Attendance Calendar (consolidated view)
  getCalendar: (month, year, countryId, search) => axios.get(`${API_URL}/hr/attendance/calendar`, { 
    params: { month, year, country_id: countryId, search } 
  }),
  
  // HR: Update individual day attendance status
  updateDayStatus: (data) => axios.post(`${API_URL}/hr/attendance/update-day`, data),
};

export const payrollApi = {
  // Payroll generation (legacy - kept for backward compatibility)
  generate: (data) => axios.post(`${API_URL}/hr/payroll/generate`, data),
  generateBulk: (data) => axios.post(`${API_URL}/hr/payroll/generate-bulk`, data),
  
  // Payroll records
  getAll: (params) => axios.get(`${API_URL}/hr/payroll`, { params }),
  getById: (payrollId) => axios.get(`${API_URL}/hr/payroll/${payrollId}`),
  getSummary: (month, year, countryId) => axios.get(`${API_URL}/hr/payroll/summary/${month}/${year}`, { params: { country_id: countryId } }),
  
  // Payment marking (legacy)
  markPaid: (payrollId, data) => axios.post(`${API_URL}/hr/payroll/${payrollId}/mark-paid`, data),
  
  // Adjustments
  createAdjustment: (payrollId, data) => axios.post(`${API_URL}/hr/payroll/${payrollId}/adjustment`, data),
  getAdjustments: (payrollId) => axios.get(`${API_URL}/hr/payroll/${payrollId}/adjustments`),
  
  // Payslip
  generatePayslip: (payrollId) => axios.post(`${API_URL}/hr/payroll/${payrollId}/generate-payslip`),
  downloadPayslip: (payrollId) => axios.get(`${API_URL}/hr/payroll/${payrollId}/payslip`, { responseType: 'blob' }),
  
  // Employee payslips (for employee modal)
  getEmployeePayslips: (employeeId) => axios.get(`${API_URL}/hr/payroll/employee/${employeeId}/payslips`),
  
  // ==================== BATCH-BASED PAYROLL (NEW GOVERNANCE) ====================
  
  // Preview payroll (no DB save)
  preview: (data) => axios.post(`${API_URL}/hr/payroll/preview`, data),
  
  // Batch management
  createBatch: (data) => axios.post(`${API_URL}/hr/payroll/batch`, data),
  getBatches: (params) => axios.get(`${API_URL}/hr/payroll/batches`, { 
    params: {
      country_id: params?.country_id,
      batch_status: params?.status,  // Map status to batch_status
      year: params?.year
    } 
  }),
  getBatch: (batchId) => axios.get(`${API_URL}/hr/payroll/batch/${batchId}`),
  
  // Update record in DRAFT batch
  updateBatchRecord: (batchId, recordId, data) => axios.put(`${API_URL}/hr/payroll/batch/${batchId}/record/${recordId}`, data),
  
  // Batch lifecycle
  confirmBatch: (batchId, data) => axios.post(`${API_URL}/hr/payroll/batch/${batchId}/confirm`, data || {}),
  markBatchPaid: (batchId, data) => axios.post(`${API_URL}/hr/payroll/batch/${batchId}/mark-paid`, data),
  deleteBatch: (batchId) => axios.delete(`${API_URL}/hr/payroll/batch/${batchId}`),
};

export const leaveApi = {
  // Apply for leave
  apply: (data) => axios.post(`${API_URL}/hr/leave/apply`, data),
  
  // My requests & balance
  getMyRequests: (year, status) => axios.get(`${API_URL}/hr/leave/my-requests`, { params: { year, leave_status: status } }),
  getMyBalance: (year) => axios.get(`${API_URL}/hr/leave/my-balance`, { params: { year } }),
  
  // Approval workflow
  getPendingApprovals: (countryId, teamId) => axios.get(`${API_URL}/hr/leave/pending-approvals`, { params: { country_id: countryId, team_id: teamId } }),
  approve: (requestId, data) => axios.post(`${API_URL}/hr/leave/${requestId}/approve`, data),
  cancel: (requestId, reason) => axios.post(`${API_URL}/hr/leave/${requestId}/cancel`, null, { params: { reason } }),
  
  // Employee leave info
  getEmployeeLeaves: (employeeId, year) => axios.get(`${API_URL}/hr/leave/employee/${employeeId}`, { params: { year } }),
  getEmployeeBalance: (employeeId, year) => axios.get(`${API_URL}/hr/leave/employee/${employeeId}/balance`, { params: { year } }),
  
  // Team summary
  getTeamSummary: (teamId, countryId) => axios.get(`${API_URL}/hr/leave/team-summary`, { params: { team_id: teamId, country_id: countryId } }),
};

// ==================== INSPECTION PACKAGES APIs ====================

export const inspectionPackagesApi = {
  // Categories
  getCategories: (countryId) => axios.get(`${API_URL}/inspection-categories`, { params: { country_id: countryId } }),
  createCategory: (data, countryId) => axios.post(`${API_URL}/inspection-categories`, data, { params: { country_id: countryId } }),
  updateCategory: (id, data) => axios.put(`${API_URL}/inspection-categories/${id}`, data),
  toggleCategoryStatus: (id) => axios.patch(`${API_URL}/inspection-categories/${id}/toggle-status`),
  deleteCategory: (id) => axios.delete(`${API_URL}/inspection-categories/${id}`),
  
  // Packages
  getPackages: (countryId) => axios.get(`${API_URL}/inspection-packages`, { params: { country_id: countryId } }),
  createPackage: (data) => axios.post(`${API_URL}/inspection-packages`, data),
  updatePackage: (id, data) => axios.put(`${API_URL}/inspection-packages/${id}`, data),
  togglePackageStatus: (id) => axios.patch(`${API_URL}/inspection-packages/${id}/toggle-status`),
  deletePackage: (id) => axios.delete(`${API_URL}/inspection-packages/${id}`),
  
  // Offers
  getOffers: (countryId) => axios.get(`${API_URL}/offers`, { params: { country_id: countryId } }),
  getActiveOffers: (countryId) => axios.get(`${API_URL}/offers/active`, { params: { country_id: countryId } }),
  createOffer: (data) => axios.post(`${API_URL}/offers`, data),
  updateOffer: (id, data) => axios.put(`${API_URL}/offers/${id}`, data),
  toggleOfferStatus: (id) => axios.patch(`${API_URL}/offers/${id}/toggle-status`),
  deleteOffer: (id) => axios.delete(`${API_URL}/offers/${id}`),
};

// Repairs Module API
export const repairsApi = {
  // Repair Parts
  getParts: (params) => axios.get(`${API_URL}/repair-parts`, { params }),
  getPartCategories: () => axios.get(`${API_URL}/repair-parts/categories`),
  getPart: (id) => axios.get(`${API_URL}/repair-parts/${id}`),
  createPart: (data) => axios.post(`${API_URL}/repair-parts`, data),
  updatePart: (id, data) => axios.put(`${API_URL}/repair-parts/${id}`, data),
  deletePart: (id) => axios.delete(`${API_URL}/repair-parts/${id}`),
  
  // Repair Rules
  getRules: (params) => axios.get(`${API_URL}/repair-rules`, { params }),
  getRule: (id) => axios.get(`${API_URL}/repair-rules/${id}`),
  createRule: (data) => axios.post(`${API_URL}/repair-rules`, data),
  updateRule: (id, data) => axios.put(`${API_URL}/repair-rules/${id}`, data),
  deleteRule: (id) => axios.delete(`${API_URL}/repair-rules/${id}`),
  
  // Inspection Questions (for rule matching)
  getInspectionQuestions: () => axios.get(`${API_URL}/inspection-qa/questions`),
  
  // Helper endpoints
  getAvailableQuestions: (countryId) => axios.get(`${API_URL}/repair-rules/available-questions`, { params: { country_id: countryId } }),
  calculateCost: (data) => axios.post(`${API_URL}/repair-parts/calculate-cost`, data),
  
  // Inspection Categories (top-level categories like Physical/Manual, OBD2, etc.)
  getInspectionCategories: (countryId) => axios.get(`${API_URL}/inspection-categories`, { params: { country_id: countryId } }),
};

// SMS Logs API
export const smsLogsApi = {
  getLogs: (params = {}) => axios.get(`${API_URL}/sms/logs`, { params }),
  getWalletBalance: () => axios.get(`${API_URL}/sms/wallet`),
  getStats: () => axios.get(`${API_URL}/sms/stats`),
  testSend: (phone) => axios.post(`${API_URL}/sms/test`, null, { params: { phone } }),
};

// Settings & Token Management API
export const settingsApi = {
  getTokenStatus: () => axios.get(`${API_URL}/settings/tokens`),
  updateToken: (tokenType, token) => axios.post(`${API_URL}/settings/tokens/update`, { token_type: tokenType, token }),
  testToken: (tokenType) => axios.post(`${API_URL}/settings/tokens/test/${tokenType}`),
  getTwilioBalance: () => axios.get(`${API_URL}/twilio/balance`),
  getFast2SmsBalance: () => axios.get(`${API_URL}/sms/wallet`),
};

// Loans API
export const loansApi = {
  // Loan Leads
  getAll: (params) => axios.get(`${API_URL}/loan-leads`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getById: (id) => axios.get(`${API_URL}/loan-leads/${id}`),
  update: (id, data) => axios.put(`${API_URL}/loan-leads/${id}`, data),
  syncCustomers: () => axios.get(`${API_URL}/loan-leads/sync-customers`),
  getStats: () => axios.get(`${API_URL}/loan-leads/stats`),
  getCardCounts: (params) => axios.get(`${API_URL}/loan-leads/card-counts`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getCities: (params) => axios.get(`${API_URL}/loan-leads/cities`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  getInspections: (id) => axios.get(`${API_URL}/loan-leads/${id}/inspections`),
  setLoanCar: (id, carNumber) => axios.put(`${API_URL}/loan-leads/${id}/loan-car`, { car_number: carNumber || null }),
  getExecutives: (params) => axios.get(`${API_URL}/loan-leads/executives`, { params: { ...params, timezone_offset: getTimezoneOffset() } }),
  assign: (id, assignedTo) => axios.put(`${API_URL}/loan-leads/${id}/assign`, { assigned_to: assignedTo || null }),
  bulkAssign: (leadIds, assignedTo) => axios.post(`${API_URL}/loan-leads/bulk-assign`, { lead_ids: leadIds, assigned_to: assignedTo || null }),

  // Activity & notes — the trail the desk leaves on a file
  getNotes: (leadId) => axios.get(`${API_URL}/loan-leads/${leadId}/notes`),
  addNote: (leadId, note) => axios.post(`${API_URL}/loan-leads/${leadId}/notes`, { note }),
  getActivities: (leadId) => axios.get(`${API_URL}/loan-leads/${leadId}/activities`),
  
  // Documents
  getDocumentRequirements: (leadId) => axios.get(`${API_URL}/loan-leads/${leadId}/document-requirements`),
  generateUploadUrl: (leadId, data) => {
    return axios.post(`${API_URL}/loan-leads/${leadId}/documents/generate-upload-url`, {
      document_type: data.document_type,
      file_name: data.filename || data.file_name,
      content_type: data.content_type
    });
  },
  uploadDocument: (leadId, data) => {
    return axios.post(`${API_URL}/loan-leads/${leadId}/documents`, {
      document_type: data.document_type,
      file_url: data.file_url,
      file_name: data.file_name
    });
  },
  getDocumentDownloadUrl: (leadId, docId) => axios.post(`${API_URL}/loan-leads/${leadId}/documents/${docId}/download-url`),
  deleteDocument: (leadId, docId) => axios.delete(`${API_URL}/loan-leads/${leadId}/documents/${docId}`),
  
  // Credit Score
  requestCreditScoreOTP: (leadId, data) => axios.post(`${API_URL}/loan-leads/${leadId}/credit-score/request-otp`, data),
  verifyCreditScoreOTP: (leadId, data) => axios.post(`${API_URL}/loan-leads/${leadId}/credit-score/verify-otp`, data),
  getCreditScore: (leadId) => axios.get(`${API_URL}/loan-leads/${leadId}/credit-score`),
  
  // Vehicles
  addVehicle: (leadId, data) => axios.post(`${API_URL}/loan-leads/${leadId}/vehicles`, data),
  updateVehicle: (leadId, vehicleId, data) => axios.put(`${API_URL}/loan-leads/${leadId}/vehicles/${vehicleId}`, data),
  removeVehicle: (leadId, vehicleId) => axios.delete(`${API_URL}/loan-leads/${leadId}/vehicles/${vehicleId}`),
  fetchVaahanForVehicle: (leadId, vehicleId) => axios.post(`${API_URL}/loan-leads/${leadId}/vehicles/${vehicleId}/fetch-vaahan`),
  
  // Loan Processing
  checkEligibility: (leadId, vehicleId) => axios.post(`${API_URL}/loan-leads/${leadId}/vehicles/${vehicleId}/check-eligibility`),
  checkVehicleBankEligibility: (leadId) => axios.post(`${API_URL}/loan-leads/${leadId}/vehicle-bank-eligibility`),
  createApplication: (leadId, data) => axios.post(`${API_URL}/loan-leads/${leadId}/applications`, data),
  updateApplication: (leadId, appId, data) => axios.put(`${API_URL}/loan-leads/${leadId}/applications/${appId}`, data),
  
  // Loan Offers
  getOffers: (leadId) => axios.get(`${API_URL}/loan-leads/${leadId}/offers`),
  getOffer: (leadId, offerId) => axios.get(`${API_URL}/loan-leads/${leadId}/offers/${offerId}`),
  createOffer: (leadId, data) => axios.post(`${API_URL}/loan-leads/${leadId}/offers`, data),
  updateOffer: (leadId, offerId, data) => axios.put(`${API_URL}/loan-leads/${leadId}/offers/${offerId}`, data),
  acceptOffer: (leadId, offerId) => axios.post(`${API_URL}/loan-leads/${leadId}/offers/${offerId}/accept`),
  addChargeToOffer: (leadId, offerId, params) => axios.post(`${API_URL}/loan-leads/${leadId}/offers/${offerId}/add-charge`, null, { params }),
  removeChargeFromOffer: (leadId, offerId, chargeType) => axios.delete(`${API_URL}/loan-leads/${leadId}/offers/${offerId}/charges/${chargeType}`),
  createManualOffer: (leadId, params) => axios.post(`${API_URL}/loan-leads/${leadId}/manual-offer`, null, { params }),
  
  // Charge Types
  getChargeTypes: (includeInactive = false) => axios.get(`${API_URL}/charge-types`, { params: { include_inactive: includeInactive } }),
  createChargeType: (data) => axios.post(`${API_URL}/charge-types`, data),
  updateChargeType: (id, params) => axios.put(`${API_URL}/charge-types/${id}`, null, { params }),
  deleteChargeType: (id) => axios.delete(`${API_URL}/charge-types/${id}`),
  
  // Customer Profile
  getProfile: (leadId) => axios.get(`${API_URL}/loan-leads/${leadId}/profile`),
  updateProfile: (leadId, data) => axios.put(`${API_URL}/loan-leads/${leadId}/profile`, data),
  analyzeBankStatement: (leadId, documentId, password = null) => {
    let url = `${API_URL}/loan-leads/${leadId}/profile/analyze-bank-statement?document_id=${documentId}`;
    if (password) {
      url += `&password=${encodeURIComponent(password)}`;
    }
    return axios.post(url);
  },
  analyzeBankStatementUrl: (fileUrl, password = null) => axios.post(`${API_URL}/loan-leads/analyze-bank-statement-url`, { file_url: fileUrl, password }),
  getAnalysisStatus: (taskId) => axios.get(`${API_URL}/loan-leads/analysis-status/${taskId}`),
  syncCreditReport: (leadId) => axios.post(`${API_URL}/loan-leads/${leadId}/profile/sync-credit-report`),
  calculateEligibility: (leadId) => axios.post(`${API_URL}/loan-leads/${leadId}/profile/calculate-eligibility`),
  
  // Credit Reports (Surepass)
  fetchCibilReport: (data) => axios.post(`${API_URL}/credit-report/cibil`, data),
  fetchEquifaxReport: (data) => axios.post(`${API_URL}/credit-report/equifax`, data),
  fetchExperianReport: (data) => axios.post(`${API_URL}/credit-report/experian`, data),
  getLatestReports: (pan) => axios.get(`${API_URL}/credit-report/latest/${pan}`),
};

// ─── Loan Profiling (Phase 1 — guided profiling cockpit) ───────────────
const LP = `${API_URL}/loan-profiling`;
const lpToken = () => localStorage.getItem('token') || sessionStorage.getItem('token') || '';
export const loanProfilingApi = {
  meta: () => axios.get(`${LP}/meta`),
  reasonCodes: () => axios.get(`${LP}/reason-codes`),
  partnerBanks: () => axios.get(`${LP}/partner-banks`),
  cockpit: (leadId) => axios.get(`${LP}/leads/${leadId}/cockpit`),
  saveCaseProfile: (leadId, body) => axios.put(`${LP}/leads/${leadId}/case-profile`, body),
  saveExistingLoan: (leadId, body) => axios.put(`${LP}/leads/${leadId}/existing-loan`, body),
  checklist: (leadId) => axios.get(`${LP}/leads/${leadId}/document-checklist`),
  uploadDocument: (leadId, docType, file, label = null) => {
    const fd = new FormData();
    fd.append('doc_type', docType);
    if (label) fd.append('label', label);
    fd.append('file', file);
    return axios.post(`${LP}/leads/${leadId}/documents/upload`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (leadId, docId) => axios.delete(`${LP}/leads/${leadId}/documents/${docId}`),
  // Warranty & service offers — the add-on package that rides with the loan
  serviceBenefits: (leadId) => axios.get(`${LP}/leads/${leadId}/service-benefits`),
  saveServiceBenefits: (leadId, body) => axios.put(`${LP}/leads/${leadId}/service-benefits`, body),
  setBenefitThreshold: (leadId, amount) => axios.put(`${LP}/leads/${leadId}/service-benefits/threshold`, { full_package_from: amount }),
  resetServiceBenefits: (leadId) => axios.post(`${LP}/leads/${leadId}/service-benefits/reset`),
  generateBenefitsLetter: (leadId) => axios.post(`${LP}/leads/${leadId}/service-benefits/letter`),
  entitlements: (leadId) => axios.get(`${LP}/leads/${leadId}/entitlements`),
  analyzeDocument: (leadId, docId, password = null) =>
    axios.post(`${LP}/leads/${leadId}/documents/${docId}/analyze${password ? `?password=${encodeURIComponent(password)}` : ''}`),
  fileUrl: (leadId, docId) => `${LP}/leads/${leadId}/documents/${docId}/file?auth=${encodeURIComponent(lpToken())}`,
  fileBlob: (leadId, docId) => axios.get(`${LP}/leads/${leadId}/documents/${docId}/file`, { responseType: 'blob' }),
  generateSummary: (leadId) => axios.post(`${LP}/leads/${leadId}/generate-summary`),
  // Shadow-underwriting (Loans Phase 1+2)
  underwriting: (leadId, params) => axios.get(`${LP}/leads/${leadId}/underwriting`, { params }),
  saveApplicationAnalysis: (leadId, body) => axios.post(`${LP}/leads/${leadId}/application-analysis/snapshot`, body || {}),
  applicationAnalysisHistory: (leadId) => axios.get(`${LP}/leads/${leadId}/application-analysis/history`),
  bankMatches: (leadId) => axios.get(`${LP}/leads/${leadId}/bank-matches`),
  getUnderwritingPolicy: () => axios.get(`${LP}/underwriting-policy`),
  updateUnderwritingPolicy: (body) => axios.put(`${LP}/underwriting-policy`, body),
  // Profile vehicles (Loan Type & Vehicle Details tab)
  inspectedVehicles: (leadId) => axios.get(`${LP}/leads/${leadId}/inspected-vehicles`),
  addVehicle: (leadId, body) => axios.post(`${LP}/leads/${leadId}/vehicles`, body),
  updateVehicle: (leadId, vehicleId, body) => axios.put(`${LP}/leads/${leadId}/vehicles/${vehicleId}`, body),
  finalizeVehicle: (leadId, vehicleId) => axios.put(`${LP}/leads/${leadId}/vehicles/${vehicleId}/finalize`),
  savePersonalDetails: (leadId, body) => axios.put(`${LP}/leads/${leadId}/customer-details/personal`, body),
  saveIncomeDetails: (leadId, body) => axios.put(`${LP}/leads/${leadId}/customer-details/income`, body),
  startBankStatementAnalysis: (leadId, params = {}) => axios.post(`${LP}/leads/${leadId}/bank-statement-analysis`, null, { params }),
  getBankStatementAnalysis: (leadId) => axios.get(`${LP}/leads/${leadId}/bank-statement-analysis`),
  removeVehicle: (leadId, vehicleId) => axios.delete(`${LP}/leads/${leadId}/vehicles/${vehicleId}`),
  fetchRc: (regNumber) => axios.get(`${API_URL}/vehicle/details/${encodeURIComponent(regNumber)}`),
  vehicleChallans: (rcNumber, forceRefresh = false) => axios.get(`${LP}/vehicles/${encodeURIComponent(rcNumber)}/challans${forceRefresh ? '?force_refresh=true' : ''}`),
  listSubmissions: (leadId) => axios.get(`${LP}/leads/${leadId}/submissions`),
  createSubmission: (leadId, body) => axios.post(`${LP}/leads/${leadId}/submissions`, body),
  updateSubmission: (leadId, subId, body) => axios.put(`${LP}/leads/${leadId}/submissions/${subId}`, body),
  // Offers Letter — bank offers, negotiation, letter, customer decision, disbursement
  listOffers: (leadId) => axios.get(`${LP}/leads/${leadId}/offers`),
  createOffer: (leadId, body) => axios.post(`${LP}/leads/${leadId}/offers`, body),
  updateOffer: (leadId, offerId, body) => axios.put(`${LP}/leads/${leadId}/offers/${offerId}`, body),
  deleteOffer: (leadId, offerId) => axios.delete(`${LP}/leads/${leadId}/offers/${offerId}`),
  addOfferRound: (leadId, offerId, body) => axios.post(`${LP}/leads/${leadId}/offers/${offerId}/rounds`, body),
  saveOfferCharges: (leadId, offerId, charges) => axios.put(`${LP}/leads/${leadId}/offers/${offerId}/charges`, { charges }),
  attachOfferDocument: (leadId, offerId, body) => axios.post(`${LP}/leads/${leadId}/offers/${offerId}/attach`, body),
  generateOfferLetter: (leadId, offerId) => axios.post(`${LP}/leads/${leadId}/offers/${offerId}/letter`),
  shareOffer: (leadId, offerId, body) => axios.post(`${LP}/leads/${leadId}/offers/${offerId}/share`, body),
  recordCustomerDecision: (leadId, offerId, body) => axios.post(`${LP}/leads/${leadId}/offers/${offerId}/customer-decision`, body),
  recordBankConfirmation: (leadId, offerId, body) => axios.post(`${LP}/leads/${leadId}/offers/${offerId}/bank-confirmation`, body),
  saveDisbursement: (leadId, offerId, body) => axios.put(`${LP}/leads/${leadId}/offers/${offerId}/disbursement`, body),
  extractDisbursement: (leadId, offerId, docId) =>
    axios.post(`${LP}/leads/${leadId}/offers/${offerId}/disbursement/extract?doc_id=${encodeURIComponent(docId)}`),
};

// Bank Master API
export const banksApi = {
  getAll: (params) => axios.get(`${API_URL}/banks`, { params }),
  getById: (id) => axios.get(`${API_URL}/banks/${id}`),
  create: (data) => axios.post(`${API_URL}/banks`, data),
  update: (id, data) => axios.put(`${API_URL}/banks/${id}`, data),
  delete: (id) => axios.delete(`${API_URL}/banks/${id}`),
  addPOC: (bankId, data) => axios.post(`${API_URL}/banks/${bankId}/poc`, data),
};

// Credit Report API (Surepass Integration)
export const creditReportApi = {
  checkStatus: () => axios.get(`${API_URL}/credit-report/check-status`),
  
  // Get latest reports for a PAN (cached data)
  getLatestReports: (pan) => axios.get(`${API_URL}/credit-report/latest/${pan}`),
  
  // CIBIL Reports
  fetchCibilReport: (data) => axios.post(`${API_URL}/credit-report/cibil`, data),
  fetchCibilPdf: (data) => axios.post(`${API_URL}/credit-report/cibil/pdf`, data),
  
  // Equifax Reports
  fetchEquifaxReport: (data) => axios.post(`${API_URL}/credit-report/equifax`, data),
  fetchEquifaxPdf: (data) => axios.post(`${API_URL}/credit-report/equifax/pdf`, data),
  
  // Experian Reports
  fetchExperianReport: (data) => axios.post(`${API_URL}/credit-report/experian`, data),
  fetchExperianPdf: (data) => axios.post(`${API_URL}/credit-report/experian/pdf`, data),
  
  // Get Reports
  getReport: (reportId) => axios.get(`${API_URL}/credit-report/${reportId}`),
  getReportHistory: (pan) => axios.get(`${API_URL}/credit-report/history/${pan}`),
  getByLoanLead: (loanLeadId) => axios.get(`${API_URL}/credit-report/by-loan-lead/${loanLeadId}`),
};

